import React, { useMemo, useState, useRef } from 'react';
import { useZunelo } from '../../context/ZuneloContext';
import { BrandIcon, Pill, Badge, Accordion, Button } from '../../ui/common';
import { MetricsService } from '../../services/metricsService';
import { useResizableInspectorWidth } from '../../hooks/useResizableInspectorWidth';
import { INSPECTOR_MAX_WIDTH_PX, INSPECTOR_MIN_WIDTH_PX } from './inspectorLayout';
import { renderInspectorEmpty } from './InspectorEmptyState';
import { useSheetDialog } from '../../hooks/useSheetDialog';
import { InspectorAgentPanel } from './InspectorAgentPanel';
import { JiraDetailModal } from '../jira/JiraDetailModal';
import { buildJiraBrowseUrl } from '../jira/jiraBrowseUrl';
import {
  itemWorkContextSnapshot,
  radarAttachmentIdForSnapshot,
  type AttentionItemContextSnapshot,
  type WorkThreadSourceStatus,
} from '../../domain/radarThreads';
import type { SourceType } from '../../domain/types';

export type InspectorVariant = 'docked' | 'sheet';

function ContextSourceRow({ src }: { src: WorkThreadSourceStatus }): React.ReactElement {
  const isToolSource = src.source === 'notes' || src.source === 'reminders';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        padding: '9px 11px',
        borderRadius: 10,
        background: 'var(--z-fill)',
        border: 'none',
      }}
    >
      {isToolSource ? (
        <span
          style={{
            width: 14,
            display: 'inline-flex',
            justifyContent: 'center',
            color: 'var(--z-dim)',
            flexShrink: 0,
          }}
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <rect x="3" y="5" width="18" height="16" rx="2" />
            <path d="M3 10h18M8 3v4M16 3v4" />
          </svg>
        </span>
      ) : (
        <BrandIcon source={src.source as SourceType} size={14} />
      )}
      <span
        style={{
          flex: '0 0 auto',
          fontSize: 12,
          fontWeight: 700,
          color: 'var(--z-text-3)',
        }}
      >
        {src.label}
      </span>
      <span
        style={{
          marginLeft: 'auto',
          fontSize: 11.5,
          color: 'var(--z-dim)',
          textAlign: 'right',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {src.detail}
      </span>
    </div>
  );
}

function InspectorContextSection({
  snapshot,
}: {
  snapshot: AttentionItemContextSnapshot;
}): React.ReactElement {
  const { thread, notes, reminders } = snapshot;
  return (
    <Accordion title="Contexto completo" defaultOpen={true}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {thread.sources.map((src) => (
          <ContextSourceRow key={src.source} src={src} />
        ))}
        {(notes.length > 0 || reminders.length > 0) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
            {notes.length > 0 ? (
              <div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'var(--z-dim)',
                    marginBottom: 5,
                  }}
                >
                  Notas vinculadas
                </div>
                {notes.map((note) => (
                  <div
                    key={note.id}
                    data-testid="inspector-context-note"
                    style={{
                      fontSize: 12.5,
                      lineHeight: 1.5,
                      color: 'var(--z-text-3)',
                      padding: '8px 10px',
                      borderRadius: 9,
                      background: 'var(--z-surface)',
                      border: '1px solid var(--z-border)',
                      marginBottom: 4,
                    }}
                  >
                    {note.text}
                  </div>
                ))}
              </div>
            ) : null}
            {reminders.length > 0 ? (
              <div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'var(--z-dim)',
                    marginBottom: 5,
                  }}
                >
                  Recordatorios
                </div>
                {reminders.map((reminder) => (
                  <div
                    key={reminder.id}
                    data-testid="inspector-context-reminder"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 12.5,
                      color: 'var(--z-text-3)',
                      padding: '8px 10px',
                      borderRadius: 9,
                      background: reminder.done ? 'var(--z-fill-2)' : 'var(--z-surface)',
                      border: '1px solid var(--z-border)',
                      marginBottom: 4,
                    }}
                  >
                    <span
                      style={{
                        flexShrink: 0,
                        color: reminder.done ? 'var(--z-ok)' : 'var(--z-warn)',
                      }}
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                      >
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 7v5l3 2" />
                      </svg>
                    </span>
                    <span
                      style={{
                        flex: '1 1 auto',
                        minWidth: 0,
                        textDecoration: reminder.done ? 'line-through' : 'none',
                      }}
                    >
                      {reminder.text}
                    </span>
                    {reminder.time ? (
                      <span
                        style={{
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: 11,
                          color: 'var(--z-dim)',
                          flexShrink: 0,
                        }}
                      >
                        {reminder.time}
                      </span>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        )}
        {thread.sources.length === 0 && notes.length === 0 && reminders.length === 0 ? (
          <span style={{ fontSize: 12, color: 'var(--z-dim)' }}>
            Sin señales cruzadas adicionales en este contexto.
          </span>
        ) : null}
      </div>
    </Accordion>
  );
}

interface InspectorShellProps {
  children: React.ReactNode;
  variant: InspectorVariant;
  onClose?: () => void;
}

const InspectorShell: React.FC<InspectorShellProps> = ({ children, variant, onClose }) => {
  const { width, isResizing, onResizePointerDown } = useResizableInspectorWidth();
  const [handleHover, setHandleHover] = useState(false);
  const isSheet = variant === 'sheet';
  const sheetRef = useSheetDialog<HTMLDivElement>({
    isOpen: isSheet,
    onClose: onClose ?? (() => {}),
    initialFocusSelector: '[data-testid="inspector-close"]',
  });
  const shellRef = useRef<HTMLDivElement | null>(null);

  return (
    <div
      id="app-inspector"
      ref={(node) => {
        if (isSheet) (sheetRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        else shellRef.current = node;
      }}
      data-testid="inspector-aside"
      data-variant={variant}
      tabIndex={isSheet ? -1 : undefined}
      aria-label="Inspector de contexto cruzado"
      aria-modal={isSheet ? true : undefined}
      role={isSheet ? 'dialog' : undefined}
      className={isSheet ? 'z-inspector-sheet' : undefined}
      style={
        isSheet
          ? {
              boxSizing: 'border-box',
              borderLeft: 'none',
              background: 'var(--z-surface)',
              overflowY: 'auto',
            }
          : {
              width,
              minWidth: INSPECTOR_MIN_WIDTH_PX,
              maxWidth: INSPECTOR_MAX_WIDTH_PX,
              flex: `0 0 ${width}px`,
              boxSizing: 'border-box',
              borderLeft: 'none',
              background: 'var(--z-surface)',
              backdropFilter: 'blur(16px)',
              overflowY: 'auto',
              position: 'relative',
              height: '100%',
              transition: isResizing ? 'none' : 'width 0.15s ease, flex-basis 0.15s ease',
            }
      }
    >
      {!isSheet && (
        <div
          data-testid="inspector-resize-handle"
          className="z-inspector-resize-handle"
          role="separator"
          aria-orientation="vertical"
          aria-valuenow={width}
          aria-valuemin={INSPECTOR_MIN_WIDTH_PX}
          aria-valuemax={INSPECTOR_MAX_WIDTH_PX}
          aria-label="Redimensionar panel del inspector"
          title="Drag to resize"
          onPointerDown={onResizePointerDown}
          onPointerEnter={() => setHandleHover(true)}
          onPointerLeave={() => {
            if (!isResizing) {
              setHandleHover(false);
            }
          }}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 8,
            zIndex: 6,
            cursor: 'col-resize',
            touchAction: 'none',
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: 'center',
            background: 'transparent',
          }}
        >
          <span
            aria-hidden
            style={{
              width: 3,
              margin: '0 auto',
              borderRadius: 99,
              alignSelf: 'stretch',
              background: handleHover || isResizing ? 'var(--z-fill-2)' : 'transparent',
              transition: 'background 0.15s ease',
              pointerEvents: 'none',
            }}
          />
        </div>
      )}
      {children}
    </div>
  );
};

function StaleSelectionSheet({
  variant,
  onClose,
}: {
  variant: InspectorVariant;
  onClose: () => void;
}): React.ReactElement {
  return (
    <InspectorShell variant={variant} onClose={onClose}>
      <div
        data-testid="inspector-stale-selection"
        style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16 }}
      >
        <p style={{ margin: 0, fontSize: 13, color: 'var(--z-muted-3)' }}>
          No encontrado. Este elemento ya no está disponible.
        </p>
        <Button data-testid="inspector-stale-selection-close" variant="soft" onClick={onClose}>
          Cerrar
        </Button>
      </div>
    </InspectorShell>
  );
}

function JiraQuickActions({
  onOpenDetail,
  jiraBrowseUrl,
}: {
  onOpenDetail: () => void;
  jiraBrowseUrl: string;
}): React.ReactElement {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      <Button
        variant="primary"
        size="sm"
        data-testid="inspector-jira-detail-btn"
        onClick={onOpenDetail}
      >
        ⛶ Ver detalle completo
      </Button>
      {jiraBrowseUrl ? (
        <a
          href={jiraBrowseUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="inspector-jira-open-external"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            height: 28,
            padding: '0 11px',
            borderRadius: 7,
            background: 'var(--z-fill)',
            color: 'var(--z-text-2)',
            fontSize: 12,
            fontWeight: 600,
            textDecoration: 'none',
          }}
        >
          <span>Abrir en Jira</span>
          <span style={{ fontSize: 10 }}>↗</span>
        </a>
      ) : null}
    </div>
  );
}

function StaleWorkflowBanner({
  why,
  toWorkflow,
  onMove,
}: {
  why: string;
  toWorkflow: string;
  onMove: () => void;
}): React.ReactElement {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 11,
        padding: '12px 14px',
        borderRadius: 13,
        background: 'var(--z-warn-bg)',
        border: 'none',
      }}
    >
      <span style={{ flex: '0 0 auto', marginTop: 1, color: 'var(--z-warn-ink)' }}>
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <path d="M12 9v4M12 17h.01M10.3 3.9 2.4 18a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
        </svg>
      </span>
      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
        <div
          style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--z-warn-ink)', marginBottom: 3 }}
        >
          El estado en Jira no refleja la realidad
        </div>
        <div
          style={{
            fontSize: 12.5,
            lineHeight: 1.55,
            color: 'var(--z-muted-2)',
            textWrap: 'pretty',
          }}
        >
          {why}
        </div>
      </div>
      <Button variant="warn" size="sm" onClick={onMove}>
        Mover a {toWorkflow}
      </Button>
    </div>
  );
}

function ExternalSignalsSection({
  signals,
}: {
  signals: ReturnType<ReturnType<typeof useZunelo>['getCrossReferences']>['externalSignals'];
}): React.ReactElement {
  return (
    <Accordion title="Contexto externo y telemetría" defaultOpen={true}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {signals.map((ext, idx) => (
          <div
            key={idx}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 9,
              padding: '9px 11px',
              borderRadius: 10,
              background: 'var(--z-fill)',
              border: 'none',
            }}
          >
            <Badge label={ext.src} tone={ext.tone} />
            <span
              style={{
                flex: '1 1 auto',
                minWidth: 0,
                fontSize: 12.5,
                color: 'var(--z-text-3)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {ext.label}
            </span>
            <span
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11,
                color: 'var(--z-dim)',
              }}
            >
              {ext.meta}
            </span>
          </div>
        ))}
      </div>
    </Accordion>
  );
}

function SiblingIssuesSection({
  siblings,
  onNavigate,
}: {
  siblings: ReturnType<ReturnType<typeof useZunelo>['getCrossReferences']>['siblings'];
  onNavigate: (source: SourceType, id: string) => void;
}): React.ReactElement {
  return (
    <Accordion title="Hermanas de este ticket" defaultOpen={true}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {siblings.map((sib, sIdx) => (
          <div
            key={sIdx}
            onClick={() => onNavigate(sib.source, sib.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 9,
              padding: '9px 11px',
              borderRadius: 10,
              background: 'var(--z-fill)',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <BrandIcon source={sib.source} size={14} />
            <Badge label={sib.tag} tone="accent" />
            <span
              style={{
                flex: '1 1 auto',
                minWidth: 0,
                fontSize: 12.5,
                color: 'var(--z-text-3)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {sib.key} — {sib.title}
            </span>
            <span
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11,
                color: 'var(--z-dim)',
              }}
            >
              {sib.age}
            </span>
          </div>
        ))}
      </div>
    </Accordion>
  );
}

interface InspectorAsideProps {
  variant?: InspectorVariant;
}

export const InspectorAside: React.FC<InspectorAsideProps> = ({ variant = 'docked' }) => {
  const {
    selectedId,
    closeContextSurface,
    getItemById,
    getAgentByName,
    getCrossReferences,
    updateWorkflowState,
    navigateToItem,
    isChatOpen,
    sendToAssistant,
    aiAttachments,
    items,
    agents,
    notes,
    reminders,
    workContextGraph,
    isLoading,
  } = useZunelo();

  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const item = selectedId ? getItemById(selectedId) : undefined;
  const snapshot = useMemo(
    () =>
      item
        ? itemWorkContextSnapshot(item, items, agents, {
            extraRelations: workContextGraph.relations,
            manualHubs: workContextGraph.manualHubs,
            notes,
            reminders,
          })
        : undefined,
    [item, items, agents, notes, reminders, workContextGraph],
  );
  const attachmentId = snapshot && item ? radarAttachmentIdForSnapshot(snapshot, item) : undefined;
  const inAssistant = attachmentId ? aiAttachments.some((att) => att.id === attachmentId) : false;

  if (isChatOpen) {
    return null;
  }

  if (!selectedId) {
    return renderInspectorEmpty(variant, InspectorShell);
  }

  // Check if it is an Agent
  const agent = getAgentByName(selectedId);
  if (agent) {
    return (
      <InspectorShell variant={variant} onClose={() => closeContextSurface()}>
        <InspectorAgentPanel
          agent={agent}
          variant={variant}
          onClose={() => closeContextSurface()}
        />
      </InspectorShell>
    );
  }

  // Attention Item: a selectedId that resolves to nothing is either a
  // genuinely stale deep link (e.g. `?item=NOPE`) or a valid one whose
  // data simply hasn't arrived yet. Only show the "not found" sheet once
  // loading has settled — otherwise a cold-loading valid deep link
  // flashes "No encontrado" before its item shows up.
  if (!item) {
    if (isLoading) {
      return null;
    }
    return <StaleSelectionSheet variant={variant} onClose={() => closeContextSurface()} />;
  }

  const crossRefs = getCrossReferences(item);
  const statusTone = MetricsService.getAttentionBadgeTone(item.st);
  const suggestion = crossRefs.suggestion;
  const isStale = suggestion && item.workflow !== suggestion.to;
  const jiraBrowseUrl =
    item.source === 'jira'
      ? buildJiraBrowseUrl(item.ticket, {
          rawSelfUrl: item.rawJiraIssue?.self,
        })
      : '';

  return (
    <>
      <InspectorShell variant={variant} onClose={() => closeContextSurface()}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <div
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 2,
              padding: '15px 46px 13px 18px',
              background: 'var(--z-surface)',
              backdropFilter: 'blur(16px)',
              borderBottom: 'none',
            }}
          >
            <button
              type="button"
              data-testid="inspector-close"
              aria-label={variant === 'sheet' ? 'Volver a la lista' : 'Quitar selección'}
              onClick={() => closeContextSurface()}
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                width: 36,
                height: 36,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'var(--z-muted-2)',
                background: 'var(--z-fill-2)',
                border: 'none',
                zIndex: 3,
              }}
            >
              ✕
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 9 }}>
              <BrandIcon source={item.source} size={16} />
              <span
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: 'var(--z-muted-2)',
                }}
              >
                {item.ticket || item.primary}
              </span>
              <Pill
                label={item.source === 'jira' ? item.workflow || 'Tareas' : item.st}
                tone={statusTone}
              />
              <span style={{ fontSize: 11.5, color: 'var(--z-faint)' }}>{item.age}</span>
            </div>
            <h2
              style={{
                margin: 0,
                fontSize: 16,
                fontWeight: 700,
                lineHeight: 1.35,
                letterSpacing: '-0.02em',
                color: 'var(--z-text)',
              }}
            >
              {item.title}
            </h2>
          </div>

          {/* Body */}
          <div
            style={{ padding: '15px 18px 34px', display: 'flex', flexDirection: 'column', gap: 12 }}
          >
            {/* AI Summary */}
            <div
              style={{
                borderRadius: 14,
                background: 'var(--z-accent-wash)',
                border: 'none',
                padding: '12px 14px',
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.09em',
                  textTransform: 'uppercase',
                  color: 'var(--z-accent-3)',
                  marginBottom: 6,
                }}
              >
                Resumen de Zunelo
              </div>
              <p
                style={{
                  margin: 0,
                  fontSize: 13,
                  lineHeight: 1.6,
                  color: 'var(--z-accent-copy)',
                  textWrap: 'pretty',
                }}
              >
                {item.ai}
              </p>
            </div>

            {snapshot ? (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button
                  variant={inAssistant ? 'soft' : 'primary'}
                  size="sm"
                  data-testid="inspector-pass-to-assistant"
                  onClick={() => {
                    if (attachmentId) sendToAssistant(snapshot.thread, attachmentId);
                  }}
                >
                  {inAssistant ? '✓ En el asistente' : 'Pasar al asistente'}
                </Button>
              </div>
            ) : null}

            {snapshot ? <InspectorContextSection snapshot={snapshot} /> : null}

            {/* Quick Actions for Jira */}
            {item.source === 'jira' ? (
              <JiraQuickActions
                onOpenDetail={() => setIsDetailOpen(true)}
                jiraBrowseUrl={jiraBrowseUrl}
              />
            ) : null}

            {/* Nudge Banner if Jira State is Out of Sync */}
            {isStale && suggestion && (
              <StaleWorkflowBanner
                why={suggestion.why}
                toWorkflow={suggestion.to}
                onMove={() => item.ticket && updateWorkflowState(item.ticket, suggestion.to)}
              />
            )}

            {/* External Signals (Sentry, AWS, Moesif) */}
            {crossRefs.externalSignals.length > 0 && (
              <ExternalSignalsSection signals={crossRefs.externalSignals} />
            )}

            {/* Connected Sibling Issues / Threads / PRs */}
            {crossRefs.siblings.length > 0 && (
              <SiblingIssuesSection siblings={crossRefs.siblings} onNavigate={navigateToItem} />
            )}
          </div>
        </div>
      </InspectorShell>
      {isDetailOpen && item.source === 'jira' ? (
        <JiraDetailModal isOpen={isDetailOpen} onClose={() => setIsDetailOpen(false)} item={item} />
      ) : null}
    </>
  );
};
